import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { ArtifactType, EdgeType, GraphEdge, GraphNode, Locale, LocalizedText, LocationStatus, MythDataset, NodeType, Story, TimelineEra, TimelineRoute, TimelineScene, TimelineSubEra } from '../data/schema'
import { copy, localize, localizeWithEnglish } from '../i18n'
import { getStoryOverviewGraph } from '../state'
import { Icon } from './Icon'
import { TypeIcon } from './TypeIcon'
import type { AtlasRelationshipBundle } from '../atlasProjection'

type InspectorTab = 'overview' | 'relationships' | 'appearances' | 'sources' | 'adaptations'
export type InspectorSnap = 'peek' | 'half' | 'full'

interface InspectorProps {
  locale: Locale
  dataset: MythDataset
  node?: GraphNode
  edge?: GraphEdge
  bundle?: AtlasRelationshipBundle
  entryContextNode?: GraphNode
  story?: Story
  storyOverview?: boolean
  stepIndex: number
  spoilerRevealed: boolean
  snap: InspectorSnap
  onReveal: () => void
  onClose?: () => void
  closeAction?: 'close' | 'back'
  onSnapChange: (snap: InspectorSnap) => void
  onDragHeightChange: (height: number | null) => void
  onSelectNode: (id: string) => void
  onSelectEdge?: (id: string) => void
  onFilterRelatedType?: (type?: NodeType) => void
  onStep: (index: number) => void
  visibleRelationshipCount?: number
  onViewAll: (id: string) => void
  onViewInStory?: (storyId: string, stepId: string) => void
  canOpenStory?: (story: Story) => boolean
  onLocateInTimeline?: (storyId: string, stepId: string) => void
  explanationMapActive?: boolean
  onShowExplanationMap?: () => void
  onCloseExplanationMap?: () => void
  timeline?: {
    route: TimelineRoute
    era: TimelineEra
    subEra: TimelineSubEra
    scene: TimelineScene
    sceneIndex: number
    onOpenChapter: (storyId: string, stepId: string) => void
  }
}

type InspectorSnapHeights = Record<InspectorSnap, number>

const artifactTypeLabels = {
  weapon: { 'zh-Hant': '武器', en: 'Weapon' },
  vehicle: { 'zh-Hant': '載具', en: 'Vehicle' },
  garment: { 'zh-Hant': '衣物', en: 'Garment' },
  container: { 'zh-Hant': '容器', en: 'Container' },
  'ritual-object': { 'zh-Hant': '儀式器物', en: 'Ritual object' },
  'quest-object': { 'zh-Hant': '任務器物', en: 'Quest object' },
  'constructed-device': { 'zh-Hant': '人造裝置', en: 'Constructed device' },
  other: { 'zh-Hant': '其他器物', en: 'Other artifact' },
} as const satisfies Record<ArtifactType, LocalizedText>

const locationStatusLabels = {
  'historically-identifiable': { 'zh-Hant': '可作歷史定位', en: 'Historically identifiable' },
  'traditional-association': { 'zh-Hant': '傳統關聯地點', en: 'Traditional association' },
  approximate: { 'zh-Hant': '約略定位', en: 'Approximate' },
  disputed: { 'zh-Hant': '位置有爭議', en: 'Disputed' },
  'mythic-realm': { 'zh-Hant': '神話領域', en: 'Mythic realm' },
  unlocatable: { 'zh-Hant': '無法定位', en: 'Unlocatable' },
} as const satisfies Record<LocationStatus, LocalizedText>

interface SheetDragState {
  pointerId: number
  startY: number
  startHeight: number
  currentHeight: number
  lastY: number
  lastTime: number
  velocityY: number
  moved: boolean
}

const inspectorSnapOrder: InspectorSnap[] = ['peek', 'half', 'full']

const normalizeTimelineText = (value: string) => value
  .toLocaleLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, '')

const ngrams = (value: string, size = 2) => {
  const normalized = normalizeTimelineText(value)
  if (normalized.length <= size) return normalized ? [normalized] : []
  return Array.from({ length: normalized.length - size + 1 }, (_, index) => normalized.slice(index, index + size))
}

function timelineTextOverlapScore(primaryText: string, comparisonText: string) {
  const primary = ngrams(primaryText)
  if (primary.length === 0) return 0
  const comparison = new Set(ngrams(comparisonText))
  if (comparison.size === 0) return 0
  const matched = primary.filter((token) => comparison.has(token)).length
  return matched / primary.length
}

function timelineDetailRepeatsNarrative(detailTexts: Array<LocalizedText | undefined>, narrativeTexts: LocalizedText[], locale: Locale) {
  const detail = detailTexts.map((item) => item ? localize(item, locale) : '').filter(Boolean).join(' ')
  const narrative = narrativeTexts.map((item) => localize(item, locale)).join(' ')
  return timelineTextOverlapScore(detail, narrative) >= 0.48
}

export function getInspectorSnapHeights(viewportHeight: number): InspectorSnapHeights {
  const peek = 104
  const half = Math.min(380, Math.max(270, viewportHeight * 0.42))
  const full = Math.max(half, viewportHeight - 180)
  return { peek, half, full }
}

export function resolveInspectorSnap(currentSnap: InspectorSnap, height: number, velocityY: number, heights: InspectorSnapHeights): InspectorSnap {
  if (Math.abs(velocityY) >= 0.45) {
    const currentIndex = inspectorSnapOrder.indexOf(currentSnap)
    const direction = velocityY < 0 ? 1 : -1
    return inspectorSnapOrder[Math.min(inspectorSnapOrder.length - 1, Math.max(0, currentIndex + direction))]
  }
  return inspectorSnapOrder.reduce((nearest, candidate) => (
    Math.abs(heights[candidate] - height) < Math.abs(heights[nearest] - height) ? candidate : nearest
  ), currentSnap)
}

const relationshipOrder: EdgeType[] = ['family', 'help', 'conflict', 'love', 'participation', 'causality', 'creation', 'possession', 'geography']

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const containsHan = (value: string) => /\p{Script=Han}/u.test(value)
const isWordCharacter = (value: string | undefined) => Boolean(value && /[\p{L}\p{N}_]/u.test(value))
const blockedEntityReferenceContinuations: Partial<Record<Locale, Record<string, string[]>>> = {
  'zh-Hant': {
    apollo: ['多洛斯'],
  },
}

interface EntityReferenceTextProps {
  text: LocalizedText
  locale: Locale
  nodes: GraphNode[]
  excludeNodeId?: string
  onSelectNode: (id: string) => void
}

export function EntityReferenceText({ text, locale, nodes, excludeNodeId, onSelectNode }: EntityReferenceTextProps) {
  const value = localize(text, locale)
  const candidatesByLabel = new Map<string, { label: string; node: GraphNode } | null>()

  nodes.filter((candidate) => candidate.id !== excludeNodeId).forEach((candidate) => {
    const labels = locale === 'en'
      ? [candidate.name.en, ...candidate.aliases]
      : [candidate.name[locale], candidate.name.en, ...candidate.aliases]
    labels.forEach((rawLabel) => {
      const label = rawLabel.trim()
      if (label.length < 2) return
      const key = label.toLocaleLowerCase()
      const existing = candidatesByLabel.get(key)
      if (!candidatesByLabel.has(key)) candidatesByLabel.set(key, { label, node: candidate })
      else if (existing?.node.id !== candidate.id) candidatesByLabel.set(key, null)
    })
  })

  const candidates = [...candidatesByLabel.values()]
    .filter((candidate): candidate is { label: string; node: GraphNode } => Boolean(candidate))
    .sort((left, right) => right.label.length - left.label.length)
  if (candidates.length === 0) return <>{value}</>

  const candidateForLabel = new Map(candidates.map((candidate) => [candidate.label.toLocaleLowerCase(), candidate]))
  const matcher = new RegExp(candidates.map((candidate) => escapeRegExp(candidate.label)).join('|'), 'giu')
  const content: ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = matcher.exec(value))) {
    const matchedLabel = match[0]
    const candidate = candidateForLabel.get(matchedLabel.toLocaleLowerCase())
    if (!candidate) continue

    const matchStart = match.index
    let matchEnd = matcher.lastIndex
    const needsWordBoundary = !containsHan(matchedLabel)
    if (needsWordBoundary && (isWordCharacter(value[matchStart - 1]) || isWordCharacter(value[matchEnd]))) continue
    if (blockedEntityReferenceContinuations[locale]?.[candidate.node.id]?.some((suffix) => value.startsWith(suffix, matchEnd))) continue
    if (matchStart < cursor) continue

    content.push(value.slice(cursor, matchStart))
    const englishName = candidate.node.name.en
    const matchedEnglishHouseName = matchedLabel.localeCompare(englishName, undefined, { sensitivity: 'accent' }) === 0
    let displayLabel = matchedLabel

    if (locale !== 'en' && !matchedEnglishHouseName) {
      const existingEnglishSuffix = value.slice(matchEnd).match(new RegExp(`^(?:\\s*[（(]\\s*${escapeRegExp(englishName)}\\s*[）)]|\\s*[·/]\\s*${escapeRegExp(englishName)})`, 'iu'))
      if (existingEnglishSuffix) {
        matchEnd += existingEnglishSuffix[0].length
        matcher.lastIndex = matchEnd
      }
      displayLabel = `${matchedLabel}（${englishName}）`
    }

    const href = `?lang=${encodeURIComponent(locale)}&mode=node&focus=${encodeURIComponent(candidate.node.id)}`
    content.push(<a
      className="entity-reference"
      data-node-id={candidate.node.id}
      href={href}
      key={`${candidate.node.id}-${matchStart}`}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        onSelectNode(candidate.node.id)
      }}
    >{displayLabel}</a>)
    cursor = matchEnd
  }

  if (content.length === 0) return <>{value}</>
  content.push(value.slice(cursor))
  return <>{content}</>
}

function EntityReferenceList({ items, ...props }: Omit<EntityReferenceTextProps, 'text'> & { items: LocalizedText[] }) {
  return <>{items.map((item, index) => <span key={`${localize(item, props.locale)}-${index}`}>{index > 0 && ' · '}<EntityReferenceText text={item} {...props} /></span>)}</>
}

export function Inspector({ locale, dataset, node, edge, bundle, entryContextNode, story, storyOverview = false, stepIndex, spoilerRevealed, snap, onReveal, onClose, closeAction = 'close', onSnapChange, onDragHeightChange, onSelectNode, onSelectEdge = () => undefined, onFilterRelatedType, onStep, visibleRelationshipCount = 0, onViewAll, onViewInStory = () => undefined, canOpenStory, onLocateInTimeline, explanationMapActive = false, onShowExplanationMap = () => undefined, onCloseExplanationMap = () => undefined, timeline }: InspectorProps) {
  const t = copy[locale]
  const [tab, setTab] = useState<InspectorTab>('overview')
  const [showAllRelationships, setShowAllRelationships] = useState(false)
  const tabRefs = useRef<Partial<Record<InspectorTab, HTMLButtonElement>>>({})
  const inspectorRef = useRef<HTMLElement>(null)
  const sheetDrag = useRef<SheetDragState | null>(null)
  const ignoreHandleClick = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const [showDragContent, setShowDragContent] = useState(false)
  const step = storyOverview ? undefined : story?.steps[stepIndex]
  const storyOverviewGraph = story && storyOverview ? getStoryOverviewGraph(dataset, story) : undefined
  const sourceFor = (ids: string[]) => ids.map((id) => dataset.sources.find((source) => source.id === id)).filter(Boolean)
  const bundleEdges = bundle
    ? bundle.edgeIds.map((id) => dataset.edges.find((item) => item.id === id)).filter((item): item is GraphEdge => Boolean(item))
    : []
  const bundleEndpoints = bundle
    ? [bundle.source, bundle.target].map((id) => dataset.nodes.find((item) => item.id === id)).filter((item): item is GraphNode => Boolean(item))
    : []
  const isSpoiler = (node?.spoilerLevel ?? edge?.spoilerLevel ?? (stepIndex > 8 ? 2 : 0)) >= 2
  const hidden = isSpoiler && !spoilerRevealed
  const mediaPath = (src: string) => `${import.meta.env.BASE_URL}${src.replace(/^\//, '')}`
  const formatName = (format: GraphNode['adaptations'][number]['format']) => {
    const names = locale === 'en'
      ? { film: 'Film', tv: 'TV', miniseries: 'Miniseries', stage: 'Stage', game: 'Game' }
      : { film: '電影', tv: '電視', miniseries: '迷你劇', stage: '舞台劇', game: '遊戲' }
    return names[format]
  }

  useEffect(() => {
    setTab('overview')
    setShowAllRelationships(false)
  }, [node?.id, edge?.id, story?.id, storyOverview, stepIndex])

  useEffect(() => {
    if (story?.id && step) inspectorRef.current?.scrollTo?.({ top: 0 })
  }, [story?.id, step?.id])

  useEffect(() => () => onDragHeightChange(null), [onDragHeightChange])

  const relationships = useMemo(() => {
    if (!node) return []
    return dataset.edges
      .filter((item) => item.source === node.id || item.target === node.id)
      .sort((a, b) => {
        const inStory = (item: GraphEdge) => story ? Number(item.storyRefs.includes(story.id) || item.contexts.some((context) => context.storyIds.includes(story.id))) : 0
        return inStory(b) - inStory(a) || relationshipOrder.indexOf(a.type) - relationshipOrder.indexOf(b.type) || a.id.localeCompare(b.id)
      })
  }, [dataset.edges, node, story])

  const belongsToStory = (relation: GraphEdge) => Boolean(story && (relation.storyRefs.includes(story.id) || relation.contexts.some((context) => context.storyIds.includes(story.id))))
  const storyRelationships = story ? relationships.filter(belongsToStory) : relationships
  const outsideRelationships = story ? relationships.filter((relation) => !belongsToStory(relation)) : []
  const outsideCycleGroups = (() => {
    const groups = new Map<string, { count: number; types: Map<EdgeType, number> }>()
    outsideRelationships.forEach((relation) => {
      const relatedStoryIds = [...new Set([...relation.storyRefs, ...relation.contexts.flatMap((context) => context.storyIds)])]
        .filter((id) => id !== story?.id)
      const cycleIds = relatedStoryIds
        .map((storyId) => dataset.stories.find((item) => item.id === storyId)?.cycle)
        .filter((id): id is string => Boolean(id))
        .sort((a, b) => dataset.storyCycles.findIndex((cycle) => cycle.id === a) - dataset.storyCycles.findIndex((cycle) => cycle.id === b))
      const cycleId = cycleIds[0] ?? 'shared-context'
      const group = groups.get(cycleId) ?? { count: 0, types: new Map<EdgeType, number>() }
      group.count += 1
      group.types.set(relation.type, (group.types.get(relation.type) ?? 0) + 1)
      groups.set(cycleId, group)
    })
    return [...groups.entries()].map(([cycleId, group]) => ({
      cycleId,
      label: cycleId === 'shared-context'
        ? locale === 'en' ? 'Shared Greek-myth context' : '共通希臘神話脈絡'
        : localize(dataset.storyCycles.find((cycle) => cycle.id === cycleId)?.name ?? { 'zh-Hant': cycleId, en: cycleId }, locale),
      ...group,
    }))
  })()
  const shownRelationships = showAllRelationships ? storyRelationships : storyRelationships.slice(0, 12)
  const relationshipGroups = relationshipOrder.map((type) => ({ type, items: shownRelationships.filter((item) => item.type === type) })).filter((group) => group.items.length)
  const appearances = node ? dataset.storyAppearances.filter((item) => item.entityId === node.id) : []
  const currentAppearance = story ? appearances.find((item) => item.storyId === story.id) : undefined
  const entryContextRelationships = node && entryContextNode
    ? dataset.edges.filter((relation) => (
      (relation.source === node.id && relation.target === entryContextNode.id)
      || (relation.target === node.id && relation.source === entryContextNode.id)
    ))
    : []
  const profileSources = node?.profile ? [...new Set([...node.sourceRefs, ...node.profile.factBlocks.flatMap((fact) => fact.sourceRefs)])] : node?.sourceRefs ?? []
  const activeEdgeContext = edge?.contexts.find((context) => Boolean(
    story
    && context.storyIds.includes(story.id)
    && (!step || context.stepIds.includes(step.id)),
  ))
    ?? edge?.contexts.find((context) => Boolean(story && context.storyIds.includes(story.id)))
    ?? edge?.contexts.find((context) => context.relationshipClass && context.explanationDecision)
  const sources = sourceFor(node
    ? profileSources
    : edge
      ? activeEdgeContext?.sourceRefs.length ? activeEdgeContext.sourceRefs : edge.sourceRefs
      : step?.sourceRefs ?? [])
  const primaryPortrait = node ? node.mediaSet?.inspectorHero ?? node.media : undefined
  const historical = node?.mediaSet?.historicalRepresentations ?? []
  const variantFacts = node?.profile?.factBlocks.filter((fact) => fact.variantLabel) ?? []
  const stepRelationships = step
    ? step.edgeIds.map((id) => dataset.edges.find((item) => item.id === id)).filter((item): item is GraphEdge => Boolean(item))
    : []
  const storyOverviewSources = storyOverviewGraph && story
    ? sourceFor([...new Set(story.steps.flatMap((storyStep) => storyStep.sourceRefs))])
    : []
  const edgeStoryContexts = edge
    ? dataset.stories.flatMap((contextStory) => contextStory.steps
      .filter((contextStep) => contextStep.edgeIds.includes(edge.id))
      .map((contextStep) => ({
        story: contextStory,
        step: contextStep,
        edgeContext: edge.contexts.find((context) => context.storyIds.includes(contextStory.id) && context.stepIds.includes(contextStep.id)),
      })))
    : []
  const timelineStory = timeline?.scene.storyId
    ? dataset.stories.find((item) => item.id === timeline.scene.storyId)
    : undefined
  const timelineStep = timelineStory && timeline?.scene.stepId
    ? timelineStory.steps.find((item) => item.id === timeline.scene.stepId)
    : undefined
  const timelineSourceStepRecords = timeline?.scene.sourceSteps.flatMap((sourceStep) => {
    const sourceStory = dataset.stories.find((item) => item.id === sourceStep.storyId)
    const sourceStoryStep = sourceStory?.steps.find((item) => item.id === sourceStep.stepId)
    return sourceStory && sourceStoryStep ? [{ story: sourceStory, step: sourceStoryStep }] : []
  }) ?? []
  const timelineTarget = timeline?.scene.primaryFocus
  const timelineNode = timelineTarget?.kind === 'node'
    ? dataset.nodes.find((item) => item.id === timelineTarget.nodeId)
    : undefined
  const timelineEdge = timelineTarget?.kind === 'edge'
    ? dataset.edges.find((item) => item.id === timelineTarget.edgeId)
    : undefined
  const timelineAppearance = timelineNode
    ? dataset.storyAppearances.find((item) => item.entityId === timelineNode.id
      && timelineSourceStepRecords.some((record) => item.storyId === record.story.id && item.stepIds.includes(record.step.id)))
    : undefined
  const timelineAppearanceContext = timelineAppearance
    ? timelineAppearance.stepContexts.find((item) => timelineSourceStepRecords.some((record) => record.step.id === item.stepId))
    : undefined
  const timelineEdgeContext = timelineEdge
    ? timelineEdge.contexts.find((context) => timelineSourceStepRecords.some((record) => context.storyIds.includes(record.story.id) && context.stepIds.includes(record.step.id)))
    : undefined
  const timelineSources = sourceFor([...new Set([
    ...(timelineEdgeContext?.sourceRefs ?? []),
    ...(timelineAppearanceContext?.sourceRefs ?? []),
    ...timelineSourceStepRecords.flatMap((record) => record.step.sourceRefs),
  ])])
  const timelineSetup = timeline?.scene.narrative?.setup ?? timelineAppearanceContext?.setup ?? timelineStep?.narrative?.setup
  const timelineDevelopment = timeline?.scene.narrative?.development
    ?? timelineAppearanceContext?.development
    ?? timelineStep?.narrative?.development
    ?? timelineStep?.summary
  const timelineConsequence = timeline?.scene.narrative?.consequence
    ?? timelineAppearanceContext?.consequence
    ?? timelineStep?.narrative?.consequence
    ?? timelineEdgeContext?.explanation?.result
  const timelineNarrativeParagraphs = [timelineSetup, timelineDevelopment, timelineConsequence]
    .filter((item): item is LocalizedText => Boolean(item))
  const hasAuthoredTimelineNarrative = Boolean(timeline?.scene.narrative && timelineNarrativeParagraphs.length > 0)
  const showTimelineEdgeCard = Boolean(timelineEdge) && !hasAuthoredTimelineNarrative && !timelineDetailRepeatsNarrative([
    timelineEdge?.summary,
    timelineEdgeContext?.explanation?.why,
    timelineEdgeContext?.explanation?.result,
  ], timelineNarrativeParagraphs, locale)
  const showTimelineNodeCard = Boolean(timelineNode)
    && !(hasAuthoredTimelineNarrative && timelineNode?.type === 'event')
    && !timelineDetailRepeatsNarrative([
    timelineAppearanceContext?.development,
    timelineAppearance?.summary,
    timelineNode?.summary,
    timelineAppearanceContext?.role,
    timelineAppearance?.role,
  ], timelineNarrativeParagraphs, locale)
  const timelineEdgeParagraphs = [
    timelineEdge?.summary,
    timelineEdgeContext?.explanation?.why,
    timelineEdgeContext?.explanation?.result,
  ].filter((item): item is LocalizedText => Boolean(item))
  const timelineSceneCount = timeline?.route.eras.reduce((total, era) => total + era.subEras.reduce((sum, subEra) => sum + subEra.scenes.length, 0), 0) ?? 0

  if (!node && !edge && !step && !storyOverviewGraph && !timeline) return null

  const tabs: Array<{ id: InspectorTab; label: string; count?: number }> = node ? [
    { id: 'overview', label: locale === 'en' ? 'Overview' : '概覽' },
    { id: 'relationships', label: locale === 'en' ? 'Relations' : '關係', count: relationships.length },
    { id: 'appearances', label: locale === 'en' ? 'Stories' : '故事', count: appearances.length },
    { id: 'sources', label: locale === 'en' ? 'Sources' : '來源', count: sources.length },
    { id: 'adaptations', label: locale === 'en' ? 'Screen' : '影劇', count: node.adaptations.length },
  ] : []
  const tabId = (id: InspectorTab) => `inspector-tab-${node?.id ?? 'entity'}-${id}`
  const panelId = (id: InspectorTab) => `inspector-panel-${node?.id ?? 'entity'}-${id}`
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | undefined
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = tabs.length - 1
    if (typeof nextIndex !== 'number') return
    event.preventDefault()
    const nextTab = tabs[nextIndex].id
    setTab(nextTab)
    tabRefs.current[nextTab]?.focus()
  }
  const handleSheetPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0) return
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight
    const startHeight = getInspectorSnapHeights(viewportHeight)[snap]
    sheetDrag.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight,
      currentHeight: startHeight,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocityY: 0,
      moved: false,
    }
    setIsDragging(true)
    onDragHeightChange(startHeight)
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const handleSheetPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = sheetDrag.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight
    const heights = getInspectorSnapHeights(viewportHeight)
    const rawHeight = drag.startHeight - (event.clientY - drag.startY)
    const resistedHeight = rawHeight < heights.peek
      ? heights.peek - (heights.peek - rawHeight) * 0.2
      : rawHeight > heights.full
        ? heights.full + (rawHeight - heights.full) * 0.2
        : rawHeight
    const elapsed = event.timeStamp - drag.lastTime
    if (elapsed > 0) drag.velocityY = (event.clientY - drag.lastY) / elapsed
    drag.currentHeight = Math.min(heights.full + 32, Math.max(heights.peek - 32, resistedHeight))
    drag.lastY = event.clientY
    drag.lastTime = event.timeStamp
    if (!drag.moved && Math.abs(event.clientY - drag.startY) > 6) {
      drag.moved = true
      setShowDragContent(true)
    }
    onDragHeightChange(drag.currentHeight)
    event.preventDefault()
  }
  const handleSheetPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = sheetDrag.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const heights = getInspectorSnapHeights(window.visualViewport?.height ?? window.innerHeight)
    const nextSnap = resolveInspectorSnap(snap, drag.currentHeight, drag.velocityY, heights)
    sheetDrag.current = null
    ignoreHandleClick.current = drag.moved || nextSnap !== snap
    setIsDragging(false)
    setShowDragContent(false)
    onSnapChange(nextSnap)
    onDragHeightChange(null)
  }
  const handleSheetPointerCancel = () => {
    sheetDrag.current = null
    setIsDragging(false)
    setShowDragContent(false)
    onDragHeightChange(null)
  }
  const handleSheetClick = () => {
    if (ignoreHandleClick.current) {
      ignoreHandleClick.current = false
      return
    }
    onSnapChange(snap === 'peek' ? 'half' : snap === 'full' ? 'half' : 'peek')
  }
  const peekTitle = hidden
    ? t.spoiler
    : bundle ? localize(bundle.label, locale)
    : node ? localize(node.name, locale)
    : edge ? localize(edge.label, locale)
    : timeline ? localize(timeline.scene.title, locale)
    : storyOverviewGraph && story ? localize(story.title, locale)
    : step ? localize(step.title, locale)
    : ''
  const peekMeta = bundle
    ? bundleEndpoints.map((item) => localize(item.name, locale)).join(' ↔ ')
    : node
    ? locale === 'en'
      ? `${t.nodeTypes[node.type]} · ${visibleRelationshipCount} of ${relationships.length} shown · ${appearances.length} stories`
      : `${node.name.en} · 目前顯示 ${visibleRelationshipCount}/${relationships.length} 項關係 · ${appearances.length} 個故事`
    : edge
      ? [edge.source, edge.target]
        .map((id) => dataset.nodes.find((item) => item.id === id))
        .filter(Boolean)
        .map((item) => localize(item!.name, locale))
        .join(' → ')
      : timeline
        ? `${localize(timeline.era.title, locale)} · ${timeline.sceneIndex}/${timelineSceneCount}`
      : storyOverviewGraph && story
        ? locale === 'en'
          ? `${story.steps.length} chapters · ${storyOverviewGraph.nodes.length} story elements · ${storyOverviewGraph.edges.length} relationships`
          : `${story.steps.length} 章 · ${storyOverviewGraph.nodes.length} 個故事項目 · ${storyOverviewGraph.edges.length} 項關係`
      : story && step
        ? locale === 'en' ? `${localize(story.title, locale)} · ${stepIndex + 1}/${story.steps.length}` : `${localize(story.title, locale)} · 第 ${stepIndex + 1}/${story.steps.length} 章`
        : ''
  const closeLabel = closeAction === 'back'
    ? storyOverview ? t.returnToOverview : locale === 'en' ? 'Back to chapter' : '返回本章'
    : locale === 'en' ? 'Close details' : '關閉詳情'

  return (
    <aside ref={inspectorRef} className={`inspector inspector-${snap}${isDragging ? ' is-dragging' : ''}${showDragContent ? ' show-drag-content' : ''}`} data-testid="inspector" data-snap={snap} data-dragging={isDragging || undefined}>
      <button
        type="button"
        className="sheet-handle"
        aria-label={locale === 'en'
          ? snap === 'peek' ? 'Expand details' : snap === 'half' ? 'Collapse details' : 'Return to half sheet'
          : snap === 'peek' ? '展開詳情' : snap === 'half' ? '收合詳情' : '返回半頁詳情'}
        aria-expanded={snap !== 'peek'}
        onClick={handleSheetClick}
        onPointerDown={handleSheetPointerDown}
        onPointerMove={handleSheetPointerMove}
        onPointerUp={handleSheetPointerUp}
        onPointerCancel={handleSheetPointerCancel}
      ><span /></button>
      <div className="mobile-peek-summary">
        {bundle ? <span className="edge-key peek-sigil bundle" /> : node ? <span className={`type-sigil peek-sigil ${node.type}`}><TypeIcon type={node.type} /></span> : edge ? <span className={`edge-key peek-sigil ${edge.type}`} /> : timelineNode ? <span className={`type-sigil peek-sigil ${timelineNode.type}`}><TypeIcon type={timelineNode.type} /></span> : timelineEdge ? <span className={`edge-key peek-sigil ${timelineEdge.type}`} /> : <span className="peek-chapter">{timeline ? String(timeline.era.order).padStart(2, '0') : storyOverview ? '00' : String(stepIndex + 1).padStart(2, '0')}</span>}
        <span className="peek-copy"><strong>{peekTitle}</strong><small>{peekMeta}</small></span>
        <button className="peek-expand" type="button" onClick={() => onSnapChange('half')}>{locale === 'en' ? 'View details' : '查看詳情'}</button>
        {onClose && !timeline && <button className="icon-button mobile-peek-close" type="button" onClick={onClose} aria-label={closeLabel}><Icon name={closeAction === 'back' ? 'arrow-left' : 'close'} /></button>}
      </div>
      <header className="inspector-head">
        <span className="eyebrow">{bundle ? locale === 'en' ? 'Story connections' : '故事關係' : timeline ? locale === 'en' ? 'Timeline · Current scene' : '時間線 · 目前場景' : story ? storyOverview ? t.storyOverview : `${t.story} · ${stepIndex + 1}/${story.steps.length}` : node ? t.nodeTypes[node.type] : edge ? t.edgeTypes[edge.type] : ''}</span>
        <span className="inspector-head-actions">
          <button className="sheet-size-button" type="button" onClick={() => onSnapChange(snap === 'full' ? 'half' : 'full')}>{locale === 'en' ? snap === 'full' ? 'Back to graph' : 'Full reading' : snap === 'full' ? '返回關係圖' : '全頁閱讀'}</button>
          {onClose && !timeline && <button className="icon-button" onClick={onClose} aria-label={closeLabel}><Icon name={closeAction === 'back' ? 'arrow-left' : 'close'} /></button>}
        </span>
      </header>
      {story && step && <nav className="story-nav story-nav-fixed" aria-label={locale === 'en' ? 'Story chapter navigation' : '故事章節導覽'}><button onClick={() => onStep(stepIndex - 1)} disabled={stepIndex === 0}><Icon name="arrow-left" />{t.previous}</button><button onClick={() => onStep(stepIndex + 1)} disabled={stepIndex === story.steps.length - 1}>{t.next}<Icon name="arrow-right" /></button></nav>}
      {hidden ? (
        <div className="spoiler-gate">
          <Icon name="spark" size={30} /><h2>{t.spoiler}</h2>
          <p>{locale === 'en' ? 'This relationship reveals the ending of the ancient story.' : '這段關係會揭示原典故事的結局。'}</p>
          <button className="primary-button" onClick={onReveal}>{t.reveal}</button>
        </div>
      ) : timeline ? (
        <section className="profile-section timeline-inspector" aria-label={locale === 'en' ? 'Current timeline narrative' : '目前時間線敘事'}>
          <div className="timeline-inspector-heading">
            <span className="timeline-scene-marker">
              <small>{locale === 'en' ? 'Era' : '階段'}</small>
              <strong>{timeline.era.order}.{timeline.subEra.order}</strong>
            </span>
            <div>
              <small>{localizeWithEnglish(timeline.subEra.title, locale)}</small>
              <h2>{localizeWithEnglish(timeline.scene.title, locale)}</h2>
              <p>{locale === 'en' ? `Scene ${timeline.sceneIndex} of ${timelineSceneCount}` : `故事章節 ${timeline.sceneIndex}／${timelineSceneCount}`}</p>
            </div>
          </div>
          <div className="timeline-scene-narrative">
            {timelineNarrativeParagraphs.map((paragraph, index) => <p key={index}>{localize(paragraph, locale)}</p>)}
          </div>
            {showTimelineNodeCard && timelineNode && <article className="timeline-target-card">
              <div className={`node-emblem ${timelineNode.type}`}><TypeIcon type={timelineNode.type} size={30} /></div>
              <div><h3>{localizeWithEnglish(timelineNode.name, locale)}</h3><p>{timelineAppearanceContext?.development ? localize(timelineAppearanceContext.development, locale) : timelineAppearance ? localize(timelineAppearance.summary, locale) : localize(timelineNode.summary, locale)}</p>{timelineAppearance && <strong>{timelineAppearanceContext?.role ? localize(timelineAppearanceContext.role, locale) : localize(timelineAppearance.role, locale)}</strong>}</div>
            </article>}
            {showTimelineEdgeCard && timelineEdge && <article className="timeline-target-card edge">
              <span className={`edge-key ${timelineEdge.type}`} />
              <div><h3>{localize(timelineEdge.label, locale)}</h3>
                {timelineEdgeParagraphs.map((paragraph, index) => <p key={index}>{localize(paragraph, locale)}</p>)}
                {timelineEdgeContext?.variantLabel && <small>{localize(timelineEdgeContext.variantLabel, locale)}</small>}
              </div>
            </article>}
            {timelineStep && <div className="timeline-story-passage">
              <span>{timelineStory && localizeWithEnglish(timelineStory.title, locale)}</span>
              <button type="button" className="text-button" onClick={() => timeline.onOpenChapter(timelineStory!.id, timelineStep.id)}>{locale === 'en' ? timeline.scene.sourceSteps.length > 1 ? 'Open related chapter' : 'Open chapter' : timeline.scene.sourceSteps.length > 1 ? '開啟相關章節' : '開啟章節'}<Icon name="arrow-right" /></button>
            </div>}
            {timeline.scene.chronologyNote && <aside className="timeline-chronology-note"><strong>{locale === 'en' ? 'Tradition note' : '版本說明'}</strong><p>{localize(timeline.scene.chronologyNote, locale)}</p></aside>}
            {timelineSources.length > 0 && <div className="source-list"><h3>{locale === 'en' ? 'Sources for this scene' : '本場景來源'}</h3>{timelineSources.map((source) => source && <a key={source.id} href={source.url} target="_blank" rel="noreferrer"><Icon name="book" size={13} /><span><strong>{localize(source.work, locale)}</strong><small>{localize(source.locator, locale)}</small></span></a>)}</div>}
        </section>
      ) : bundle ? (
        <section className="profile-section relationship-bundle-inspector" aria-label={locale === 'en' ? 'Relationship instances' : '關係實例'}>
          <h2>{bundleEndpoints.map((item) => localize(item.name, locale)).join(' ↔ ')}</h2>
          <p>{locale === 'en'
            ? 'Each connection belongs to a particular story, event or source tradition. Choose one to see its meaning and sources.'
            : '每段關係只屬於特定故事、事件或來源版本。請選擇一項，查看它的意思和出處。'}</p>
          <div className="relationship-list">
            {bundleEdges.map((relation) => {
              const storyNames = [...new Set([
                ...relation.storyRefs,
                ...relation.contexts.flatMap((context) => context.storyIds),
              ])].map((storyId) => dataset.stories.find((item) => item.id === storyId))
                .filter((item): item is Story => Boolean(item))
                .map((item) => localize(item.title, locale))
              return <button key={relation.id} type="button" onClick={() => onSelectEdge(relation.id)}>
                <span className={`edge-key ${relation.type}`} />
                <span>
                  <strong>{localize(relation.label, locale)}</strong>
                  <small>{storyNames.join(' · ') || (locale === 'en' ? 'Source-specific context' : '特定來源脈絡')}</small>
                </span>
                <Icon name="chevron" size={16} />
              </button>
            })}
          </div>
        </section>
      ) : node ? (
        <>
          <div className="profile-identity">
            {primaryPortrait ? (
              <figure className={`node-portrait ${node.type}`}>
                <img src={mediaPath(primaryPortrait.src)} alt={localize(primaryPortrait.alt, locale)} />
                {primaryPortrait.kind !== 'ai-generated' && <figcaption>{primaryPortrait.credit} · {primaryPortrait.license}</figcaption>}
              </figure>
            ) : <div className={`node-emblem ${node.type}`}><TypeIcon type={node.type} size={34} /></div>}
            <div>
              <h2>{localize(node.name, locale)}</h2>
              {(locale !== 'en' || node.profile?.originalName) && <div className="alternate-name">{locale === 'en' ? node.profile?.originalName : node.name.en}{locale !== 'en' && (node.profile?.originalName ? ` · ${node.profile.originalName}` : node.aliases[0] ? ` · ${node.aliases[0]}` : '')}</div>}
            </div>
          </div>
          {currentAppearance && (
            <div className="context-banner">
              <span>{locale === 'en' ? 'In this story' : '在這個故事中'}</span>
              <strong><EntityReferenceText text={currentAppearance.role} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></strong>
              <p><EntityReferenceText text={currentAppearance.summary} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p>
            </div>
          )}
          {!currentAppearance && entryContextNode && (
            <div className="context-banner">
              <span>{entryContextNode.type === 'place'
                ? locale === 'en' ? 'Associated with this place' : '與此地點的關係'
                : entryContextNode.type === 'event'
                  ? locale === 'en' ? 'Role in this event' : '在此事件中的角色'
                  : locale === 'en' ? 'Current exploration context' : '目前探索脈絡'}</span>
              <strong>{localize(entryContextNode.name, locale)}</strong>
              <p>{entryContextRelationships.length
                ? entryContextRelationships.map((relation) => localize(relation.summary, locale)).join(' ')
                : locale === 'en' ? 'Continue through the myth atlas to find the related story context.' : '可在神話地圖繼續探索相關故事脈絡。'}</p>
            </div>
          )}
          {onFilterRelatedType && <nav className="inspector-atlas-actions" aria-label={locale === 'en' ? 'Atlas actions' : '神話地圖操作'}>
            {node.type !== 'place' && <button type="button" onClick={() => onFilterRelatedType('place')}>{locale === 'en' ? 'Show related places' : '顯示相關地點'}</button>}
            {node.type !== 'event' && <button type="button" onClick={() => onFilterRelatedType('event')}>{locale === 'en' ? 'Show related events' : '顯示相關事件'}</button>}
          </nav>}
          <div className="inspector-tabs" role="tablist" aria-label={locale === 'en' ? 'Profile sections' : '人物分頁'}>
            {tabs.map((item, index) => <button
              key={item.id}
              ref={(element) => { tabRefs.current[item.id] = element ?? undefined }}
              id={tabId(item.id)}
              role="tab"
              aria-selected={tab === item.id}
              aria-controls={panelId(item.id)}
              tabIndex={tab === item.id ? 0 : -1}
              className={tab === item.id ? 'active' : ''}
              onClick={() => setTab(item.id)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >{item.label}{typeof item.count === 'number' && <span>{item.count}</span>}</button>)}
          </div>

          {tab === 'overview' && (
            <section className="profile-section" id={panelId('overview')} role="tabpanel" aria-labelledby={tabId('overview')} tabIndex={0}>
              <p className="inspector-summary"><EntityReferenceText text={node.summary} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p>
              {node.artifactProfile && <div className="profile-meta-grid">
                <div><small>{locale === 'en' ? 'Artifact type' : '器物類型'}</small><p>{localize(artifactTypeLabels[node.artifactProfile.artifactType], locale)}</p></div>
                <div><small>{locale === 'en' ? 'Origin' : '起源'}</small><p>{localize(node.artifactProfile.originSummary, locale)}</p></div>
              </div>}
              {node.locationStatus && <div className="profile-meta-grid">
                <div><small>{locale === 'en' ? 'Location basis' : '位置依據'}</small><p>{localize(locationStatusLabels[node.locationStatus], locale)}</p></div>
              </div>}
              {node.profile ? <>
                <p className="profile-overview"><EntityReferenceText text={node.profile.overview} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p>
                <div className="profile-meta-grid">
                  {node.profile.epithets.length > 0 && <div><small>{locale === 'en' ? 'Epithets' : '稱號'}</small><p><EntityReferenceList items={node.profile.epithets} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p></div>}
                  {node.profile.domains.length > 0 && <div><small>{locale === 'en' ? 'Domains' : '領域'}</small><p><EntityReferenceList items={node.profile.domains} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p></div>}
                  {node.profile.symbols.length > 0 && <div><small>{locale === 'en' ? 'Symbols' : '象徵'}</small><p><EntityReferenceList items={node.profile.symbols} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p></div>}
                </div>
                <div className="fact-list">
                  {node.profile.factBlocks.map((fact) => fact.spoilerLevel >= 2 && !spoilerRevealed ? (
                    <button key={fact.id} className="fact-spoiler" onClick={onReveal}><Icon name="spark" size={15} />{locale === 'en' ? 'Reveal spoiler content' : '顯示劇透內容'}</button>
                  ) : <article key={fact.id}>
                    <h3><EntityReferenceText text={fact.title} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></h3>
                    <p><EntityReferenceText text={fact.body} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p>
                    {fact.variantLabel && <small><EntityReferenceText text={fact.variantLabel} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></small>}
                    <div className="fact-citations" aria-label={locale === 'en' ? 'Sources for this detail' : '本段來源'}>
                      {sourceFor(fact.sourceRefs).map((source) => source && <a key={source.id} href={source.url} target="_blank" rel="noreferrer"><Icon name="book" size={12} />{localize(source.locator, locale)}</a>)}
                    </div>
                  </article>)}
                </div>
              </> : <div className="profile-empty">{locale === 'en' ? 'A detailed profile is being prepared.' : '人物詳情仍在整理。'}</div>}
            </section>
          )}

          {tab === 'relationships' && (
            <section className="profile-section" id={panelId('relationships')} role="tabpanel" aria-labelledby={tabId('relationships')} tabIndex={0}>
              <div className="relationship-coverage" aria-live="polite">
                <span><strong>{visibleRelationshipCount}</strong> / {relationships.length}</span>
                <p>{locale === 'en' ? 'connections currently shown' : '項關係目前顯示'}</p>
              </div>
              {relationshipGroups.map((group) => <div className="relationship-group" key={group.type}><h3>{t.edgeTypes[group.type]}</h3><div className="relationship-list">{group.items.map((relation) => {
                const otherId = relation.source === node.id ? relation.target : relation.source
                const other = dataset.nodes.find((item) => item.id === otherId)
                const inCurrentStory = belongsToStory(relation)
                return other ? <button key={relation.id} onClick={() => onSelectEdge(relation.id)}><span className={`edge-key ${relation.type}`} /><span><strong>{localize(other.name, locale)}{locale !== 'en' && <> <em>{other.name.en}</em></>}</strong><small>{localize(relation.label, locale)}{inCurrentStory ? ` · ${locale === 'en' ? 'this story' : '這個故事'}` : ''}</small></span><Icon name="chevron" size={16} /></button> : null
              })}</div></div>)}
              {!showAllRelationships && storyRelationships.length > shownRelationships.length && <button className="overflow-button" onClick={() => setShowAllRelationships(true)}>{locale === 'en' ? `Show ${storyRelationships.length - shownRelationships.length} more connections` : `顯示另外 ${storyRelationships.length - shownRelationships.length} 項關係`}</button>}
              {story && outsideRelationships.length > 0 && <div className="outside-story-preview">
                <div><span>{locale === 'en' ? 'Beyond this story' : '這個故事以外'}</span><h3>{locale === 'en' ? 'Connections outside this story' : '其他神話中的關係'}</h3><p>{locale === 'en' ? 'This figure also appears in other Greek myths.' : '這個人物也出現在其他希臘神話故事中。'}</p></div>
                <div className="outside-cycle-groups" aria-label={locale === 'en' ? 'Other connections grouped by myth cycle' : '按神話系列整理的其他關係'}>{outsideCycleGroups.map((group) => <div key={group.cycleId}>
                  <strong>{group.label}</strong>
                  <span>{locale === 'en' ? `${group.count} ${group.count === 1 ? 'connection' : 'connections'}` : `${group.count} 項關係`} · {[...group.types.entries()].map(([type, count]) => `${t.edgeTypes[type]} ${count}`).join(' · ')}</span>
                </div>)}</div>
                <div className="relationship-list">{outsideRelationships.slice(0, 3).map((relation) => {
                  const otherId = relation.source === node.id ? relation.target : relation.source
                  const other = dataset.nodes.find((item) => item.id === otherId)
                  return other ? <button key={relation.id} onClick={() => onSelectEdge(relation.id)}><span className={`edge-key ${relation.type}`} /><span><strong>{localize(other.name, locale)}{locale !== 'en' && <> <em>{other.name.en}</em></>}</strong><small>{localize(relation.label, locale)}</small></span><Icon name="chevron" size={16} /></button> : null
                })}</div>
                <button className="text-button view-all-connections" onClick={() => onViewAll(node.id)}>{locale === 'en' ? `View all ${relationships.length} connections` : `查看全部 ${relationships.length} 項關係`}<Icon name="arrow-right" /></button>
              </div>}
            </section>
          )}

          {tab === 'appearances' && (
            <section className="profile-section appearance-timeline" id={panelId('appearances')} role="tabpanel" aria-labelledby={tabId('appearances')} tabIndex={0}>
              {appearances.map((appearance) => {
                const appearanceStory = dataset.stories.find((item) => item.id === appearance.storyId)
                return <article key={appearance.id} className={story?.id === appearance.storyId ? 'current' : ''}><span>{appearanceStory ? localize(appearanceStory.title, locale) : appearance.storyId}</span><h3><EntityReferenceText text={appearance.role} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></h3><p><EntityReferenceText text={appearance.summary} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></p><small>{appearance.stepIds.length} {locale === 'en' ? 'chapters' : '個章節'}</small></article>
              })}
            </section>
          )}

          {tab === 'sources' && (
            <section className="profile-section" id={panelId('sources')} role="tabpanel" aria-labelledby={tabId('sources')} tabIndex={0}>
              {variantFacts.length > 0 && <div className="variant-notes"><h3>{locale === 'en' ? 'Tradition and version notes' : '傳承與版本註記'}</h3>{variantFacts.map((fact) => <article key={fact.id}>
                <strong><EntityReferenceText text={fact.title} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></strong>
                <span><EntityReferenceText text={fact.variantLabel!} locale={locale} nodes={dataset.nodes} excludeNodeId={node.id} onSelectNode={onSelectNode} /></span>
                <div>{sourceFor(fact.sourceRefs).map((source) => source && <a key={source.id} href={source.url} target="_blank" rel="noreferrer">{localize(source.locator, locale)}</a>)}</div>
              </article>)}</div>}
              <SourceList locale={locale} sources={sources} />
              {historical.length > 0 && <details className="historical-gallery">
                <summary><span>{locale === 'en' ? 'Historical representations' : '歷史藝術形象'}</span><small>{historical.length}</small><Icon name="chevron" size={16} /></summary>
                <div className="historical-gallery-content">{historical.map((asset) => <figure key={asset.src}><img src={mediaPath(asset.src)} alt={localize(asset.alt, locale)} /><figcaption><strong>{asset.credit}</strong><span>{asset.license}</span></figcaption></figure>)}</div>
              </details>}
            </section>
          )}

          {tab === 'adaptations' && (
            <section className="profile-section adaptation-section" id={panelId('adaptations')} role="tabpanel" aria-labelledby={tabId('adaptations')} tabIndex={0}>
              {node.adaptations.length > 0 ? <div className="adaptation-list">{node.adaptations.map((adaptation) => <a key={adaptation.id} href={adaptation.externalUrl} target="_blank" rel="noreferrer"><span className="adaptation-copy"><small>{formatName(adaptation.format)} · {adaptation.year}</small><strong>{localize(adaptation.title, locale)}</strong><span>{localize(adaptation.characterName, locale)}{adaptation.performer ? ` · ${t.portrayedBy}: ${adaptation.performer}` : ''}</span></span><Icon name="chevron" size={16} /></a>)}</div> : <div className="profile-empty">{locale === 'en' ? 'No screen adaptations have been added yet.' : '尚未加入相關影劇。'}</div>}
            </section>
          )}
        </>
      ) : story && storyOverviewGraph && !edge ? (
        <>
          <div className="story-overview-mark"><Icon name="myths" size={24} /><span>{t.storyOverview}</span></div>
          <h2>{localize(story.title, locale)}</h2>
          <p className="inspector-summary">{localize(story.description, locale)}</p>
          <div className="story-overview-stats" aria-label={locale === 'en' ? 'Story at a glance' : '故事概要'}>
            <div><strong>{story.steps.length}</strong><span>{t.chapters}</span></div>
            <div><strong>{storyOverviewGraph.nodes.length}</strong><span>{locale === 'en' ? 'People, places & events' : '人物／地點／事件'}</span></div>
            <div><strong>{storyOverviewGraph.edges.length}</strong><span>{locale === 'en' ? 'Relationships' : '關係'}</span></div>
          </div>
          <p className="story-overview-warning"><Icon name="spark" size={15} />{t.storyOverviewHint}</p>
          <button type="button" className="primary-button story-start-button" onClick={() => onStep(0)}>{t.startStory}<Icon name="arrow-right" size={16} /></button>

          <section className="story-overview-section" aria-labelledby="story-overview-chapters">
            <h3 id="story-overview-chapters">{t.chapters} · {story.steps.length}</h3>
            <div className="story-chapter-list">{story.steps.map((storyChapter, index) => <button type="button" key={storyChapter.id} onClick={() => onStep(index)}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <span><strong>{localize(storyChapter.title, locale)}</strong><small>{localize(storyChapter.summary, locale)}</small></span>
              <Icon name="chevron" size={16} />
            </button>)}</div>
          </section>

          <section className="story-overview-section" aria-labelledby="story-overview-nodes">
            <h3 id="story-overview-nodes">{locale === 'en' ? `People, places and events · ${storyOverviewGraph.nodes.length}` : `人物、地點與事件 · ${storyOverviewGraph.nodes.length}`}</h3>
            <div className="story-people story-overview-people">{storyOverviewGraph.nodes.map((item) => <button key={item.id} onClick={() => onSelectNode(item.id)}><span className={`type-sigil ${item.type}`}><TypeIcon type={item.type} /></span><span>{localize(item.name, locale)}{locale !== 'en' && <small>{item.name.en}</small>}</span></button>)}</div>
          </section>

          {storyOverviewGraph.edges.length > 0 && <section className="story-connections story-overview-section" aria-label={t.storyMap}>
            <h3>{t.storyMap} · {storyOverviewGraph.edges.length}</h3>
            <div>{storyOverviewGraph.edges.map((relation) => {
              const sourceNode = dataset.nodes.find((item) => item.id === relation.source)
              const targetNode = dataset.nodes.find((item) => item.id === relation.target)
              const partyName = (item?: GraphNode) => item ? `${localize(item.name, locale)}${locale !== 'en' ? ` · ${item.name.en}` : ''}` : ''
              return <button type="button" className="story-connection-card" key={relation.id} onClick={() => onSelectEdge(relation.id)}>
                <span className={`edge-key ${relation.type}`} />
                <div><strong>{localize(relation.label, locale)}</strong><small>{partyName(sourceNode)} {relation.directed ? '→' : '—'} {partyName(targetNode)}</small><p>{localize(relation.summary, locale)}</p></div>
              </button>
            })}</div>
          </section>}
          <SourceList locale={locale} sources={storyOverviewSources} />
        </>
      ) : story && step && !edge ? (
        <>
          <div className="chapter-mark"><span>{String(stepIndex + 1).padStart(2, '0')}</span><div /></div>
          <h2>{localize(step.title, locale)}</h2>
          <p className="inspector-summary">{localize(step.summary, locale)}</p>
          {onLocateInTimeline && <button type="button" className="locate-in-timeline" onClick={() => onLocateInTimeline(story.id, step.id)}>
            <Icon name="spark" size={15} />
            <span>{locale === 'en' ? 'Locate in Timeline' : '在時間線中定位'}</span>
          </button>}
          <div className="story-people">{step.nodeIds.map((id) => dataset.nodes.find((item) => item.id === id)).filter(Boolean).map((item) => <button key={item!.id} onClick={() => onSelectNode(item!.id)}><span className={`type-sigil ${item!.type}`}><TypeIcon type={item!.type} /></span><span>{localize(item!.name, locale)}{locale !== 'en' && <small>{item!.name.en}</small>}</span></button>)}</div>
          {stepRelationships.length > 0 && <section className="story-connections" aria-label={locale === 'en' ? 'Relationships in this chapter' : '本章關係脈絡'}>
            <h3>{locale === 'en' ? `Relationships in this chapter · ${stepRelationships.length}` : `本章關係脈絡 · ${stepRelationships.length}`}</h3>
            <div>{stepRelationships.map((relation) => {
              const sourceNode = dataset.nodes.find((item) => item.id === relation.source)
              const targetNode = dataset.nodes.find((item) => item.id === relation.target)
              const partyName = (item?: GraphNode) => item ? `${localize(item.name, locale)}${locale !== 'en' ? ` · ${item.name.en}` : ''}` : ''
              return <button type="button" className="story-connection-card" key={relation.id} onClick={() => onSelectEdge(relation.id)}>
                <span className={`edge-key ${relation.type}`} />
                <div><strong>{localize(relation.label, locale)}</strong><small>{partyName(sourceNode)} {relation.directed ? '→' : '—'} {partyName(targetNode)}</small><p>{localize(relation.summary, locale)}</p></div>
              </button>
            })}</div>
          </section>}
          <SourceList locale={locale} sources={sources} />
        </>
      ) : edge ? (
        <>
          <div className={`edge-hero ${edge.type}`}><span /><Icon name="arrow-right" /></div>
          <h2>{localize(edge.label, locale)}</h2>
          <div className="edge-parties">{[edge.source, edge.target].map((id) => dataset.nodes.find((item) => item.id === id)).filter(Boolean).map((item) => <button key={item!.id} onClick={() => onSelectNode(item!.id)}><span className={`type-sigil ${item!.type}`}><TypeIcon type={item!.type} /></span><span>{localize(item!.name, locale)}{locale !== 'en' && <small>{item!.name.en}</small>}</span></button>)}</div>
          {story && (step || storyOverview) && <div className="context-banner edge-story-banner">
            <span>{locale === 'en' ? 'In this story' : '在這個故事中'}</span>
            <strong>{localize(story.title, locale)} · {storyOverview ? t.storyOverview : localize(step!.title, locale)}</strong>
          </div>}
          <section className="edge-reading" aria-label={locale === 'en' ? 'Relationship explanation' : '關係解釋'}>
            <div className="edge-reading-section">
              <span className="edge-reading-label">{locale === 'en' ? 'What happened' : '發生了什麼'}</span>
              <p className="inspector-summary">{localize(edge.summary, locale)}</p>
            </div>
            {activeEdgeContext?.explanation?.why && <div className="edge-reading-section">
              <span className="edge-reading-label">{locale === 'en' ? 'Why / Context' : '原因／脈絡'}</span>
              <p>{localize(activeEdgeContext.explanation.why, locale)}</p>
            </div>}
            {activeEdgeContext?.explanation?.result && <div className="edge-reading-section">
              <span className="edge-reading-label">{locale === 'en' ? 'Result' : '結果'}</span>
              <p>{localize(activeEdgeContext.explanation.result, locale)}</p>
            </div>}
            {activeEdgeContext?.variantLabel && <p className="edge-variant-note"><strong>{locale === 'en' ? 'Version note' : '版本註記'}</strong>{localize(activeEdgeContext.variantLabel, locale)}</p>}
            {activeEdgeContext?.explanationGraph && <button
              type="button"
              className="explanation-map-button"
              aria-pressed={explanationMapActive}
              onClick={explanationMapActive ? onCloseExplanationMap : onShowExplanationMap}
            ><Icon name={explanationMapActive ? 'close' : 'spark'} size={15} />{explanationMapActive
                ? locale === 'en' ? 'Return to relationship graph' : '返回關係圖'
                : locale === 'en' ? 'Show explanation map' : '顯示關係解說圖'}</button>}
          </section>
          {edgeStoryContexts.length > 0 && <section className="edge-contexts" aria-label={locale === 'en' ? 'Where this relationship appears' : '這段關係出現在哪些故事'}>
            <h3>{locale === 'en' ? `Where this relationship appears · ${edgeStoryContexts.length}` : `出現故事 · ${edgeStoryContexts.length}`}</h3>
            <div>{edgeStoryContexts.map((context) => <article key={`${context.story.id}-${context.step.id}`} className={story?.id === context.story.id && step?.id === context.step.id ? 'current' : ''}>
              <small>{localize(context.story.title, locale)}</small>
              <strong>{localize(context.step.title, locale)}</strong>
              {context.edgeContext?.variantLabel && <span className="edge-context-variant">{localize(context.edgeContext.variantLabel, locale)}</span>}
              <p>{localize(context.step.summary, locale)}</p>
              {(canOpenStory ? canOpenStory(context.story) : context.story.status === 'available') && <button type="button" className="view-in-story-button" onClick={() => onViewInStory(context.story.id, context.step.id)}>{locale === 'en' ? 'View in story' : '在故事中查看'}<Icon name="arrow-right" size={14} /></button>}
            </article>)}</div>
          </section>}
          <SourceList locale={locale} sources={sources} />
        </>
      ) : null}
      <AdSlot />
    </aside>
  )
}

function SourceList({ locale, sources }: { locale: Locale; sources: Array<MythDataset['sources'][number] | undefined> }) {
  const t = copy[locale]
  return <section className="source-block"><h3><Icon name="book" size={16} />{t.sources}</h3>{sources.map((source) => source && <a key={source.id} href={source.url} target="_blank" rel="noreferrer"><strong>{localize(source.work, locale)}</strong><span>{localize(source.author, locale)} · {localize(source.locator, locale)}</span></a>)}</section>
}

export function AdSlot() {
  const enabled = false
  return enabled ? <div className="ad-slot" data-ad-slot="inspector">Advertisements</div> : null
}
